import { Time } from '@angular/common';
import * as dayjs from 'dayjs';
import { ValueTransformer } from 'typeorm';
import { OrderDeliveryStatusEnum } from '../../../../../data/src/lib/dto/orderDeliveryDateStatus.enum.dto';

export type MySqlTime = {
  hours: number;
  minutes: number;
  seconds: number;
};

export function hasDST(date = new Date()) {
  const january = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
  const july = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();

  return Math.max(january, july) !== date.getTimezoneOffset();
}

export function GetTimeStringFromDate(date: Date): string {
  return `${('0' + date.getUTCHours()).slice(
    -2
  )}:${date.getUTCMinutes()}:${date.getUTCSeconds()}`;
}

export function castDateAsInt(d: Date) {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

export function addDaysToDate(date: Date, days: number): Date {
  date.setDate(date.getDate() + days);
  return date;
}

export function removeDaysFromDate(date: Date, days: number): Date {
  date.setDate(date.getDate() - days);
  return date;
}

export function timeToDate(t: Time, useUtc: boolean = false, date: Date = new Date()) {
  const d: Date = new Date(date);
  if (useUtc) {
    d.setUTCHours(t.hours);
    d.setUTCMinutes(t.minutes);
    d.setUTCSeconds(0);
    d.setUTCMilliseconds(0);
  } else {
    d.setHours(t.hours);
    d.setMinutes(t.minutes);
    d.setSeconds(0);
    d.setMilliseconds(0);
  }
  return d;
}

export function dateToTime(d: Date, useUtc: boolean = false) {
  let t: Time;
  if (useUtc) {
    t = { hours: d.getUTCHours(), minutes: d.getUTCMinutes() } as Time;
  } else {
    t = { hours: d.getHours(), minutes: d.getMinutes() } as Time;
  }
  return t;
}

export function substractMonthsFromDate(
  date = new Date(),
  numOfMonths: number
) {
  const dateCopy = new Date(date.getTime());
  dateCopy.setMonth(dateCopy.getMonth() - numOfMonths);
  return dateCopy;
}

export function substractDaysFromDate(date = new Date(), days: number) {
  const dateCopy = new Date(date.getTime());
  dateCopy.setDate(dateCopy.getDate() - days);
  return dateCopy;
}

export function addDaysFromDate(date = new Date(), days: number) {
  const dateCopy = new Date(date.getTime());
  dateCopy.setDate(dateCopy.getDate() + days);
  return dateCopy;
}

export function timeDiffFromNowAsDateAndTime(
  date: Date,
  now?: Date
): [Date, MySqlTime] {
  if (!now) {
    now = newDateUTC();
  }
  const diffInMs = Math.abs(new Date(now).getTime() - new Date(date).getTime());
  const minDate = new Date('1000-01-01T00:00:00'); // Minimum date for MySQL DATETIME is 1000-01-01T00:00:00 but putting lower values should work
  let differenceDate = new Date(minDate.getTime() + diffInMs);
  // Remove initial
  // years: 1000
  // months: 1
  // days: 1
  differenceDate.setFullYear(differenceDate.getFullYear() - 1000);
  differenceDate.setMonth(differenceDate.getMonth() - 1);
  differenceDate.setDate(differenceDate.getDate() - 1);
  const diffDate =
    differenceDate.getFullYear() < 0
      ? null
      : new Date(differenceDate.toISOString().split('T')[0]);
  const diffTime = {
    hours: differenceDate.getHours(),
    minutes: differenceDate.getMinutes(),
    seconds: differenceDate.getSeconds()
  } as MySqlTime;

  return [diffDate, diffTime];
}

export function timeDiff(t1: Time, t2: Time, useUtc: boolean = false) {
  const d1: Date = new Date();
  const d2: Date = new Date();

  d1.setSeconds(0);
  d2.setSeconds(0);
  d1.setMilliseconds(0);
  d2.setMilliseconds(0);

  d1.setHours(t1.hours);
  d1.setMinutes(t1.minutes);

  d2.setHours(t2.hours);
  d2.setMinutes(t2.minutes);

  if (d2.getHours() < d1.getHours()) {
    d2.setDate(d1.getDate() + 1);
  }
  return d2.getTime() - d1.getTime();
}

export const timeTransformer: ValueTransformer = {
  from: (value: string) => {
    const q = typeof value === 'string' ? value.split(':') : [];
    return q.length >= 2 ? { hours: q[0], minutes: q[1] } : null;
  },
  to: (value: Time) => (value ? `${value.hours}:${value.minutes}:00` : null)
};

export const mySqlTimeTransformer: ValueTransformer = {
  from: (value: string) => {
    const q = typeof value === 'string' ? value.split(':') : [];
    return q.length >= 2
      ? { hours: q[0], minutes: q[1], seconds: q.length > 2 ? q[2] : 0 }
      : null;
  },
  to: (value: MySqlTime) =>
    value ? `${value.hours}:${value.minutes}:${value.seconds}` : null
};

export function addMinutesToDate(date: Date, minutes: number): Date {
  date.setMinutes(date.getMinutes() + minutes);
  return date;
}

export function subtractMinutesFromDate(date: Date, minutes: number): Date {
  date.setMinutes(date.getMinutes() - minutes);
  return date;
}

export function castUTCDateTimeAsBigInt(dateNow: Date) {
  return (
    dateNow.getUTCFullYear() * 100000000 +
    (dateNow.getUTCMonth() + 1) * 1000000 +
    dateNow.getUTCDate() * 10000 +
    dateNow.getUTCHours() * 100 +
    dateNow.getUTCMinutes()
  );
}

export function castDateTimeAsBigInt(dateNow: Date) {
  return (
    dateNow.getFullYear() * 100000000 +
    (dateNow.getMonth() + 1) * 1000000 +
    dateNow.getDate() * 10000 +
    dateNow.getHours() * 100 +
    dateNow.getMinutes()
  );
}

export function formatIntMinutesToDateDescription(time: number): string {
  let result = '';
  // tslint:disable-next-line: no-bitwise
  const format = (n: number) => `${(n / 60) ^ 0}:` + (n % 60);
  const preorderTime = format(time);
  const hours = parseInt(preorderTime.split(':')[0], 10);
  const minutes = parseInt(preorderTime.split(':')[1], 10);

  if (hours > 1) {
    result = hours + ' hours';
  } else if (hours === 1) {
    result = hours + ' hour';
  }

  if (hours > 0 && minutes > 0) {
    result += ' and ';
  }

  if (minutes > 1) {
    result += minutes + ' minutes';
  } else if (minutes === 1) {
    result += minutes + ' minute';
  }

  return result;
}

export function checkPreparationTime(time: number): Date {
  const date: Date = new Date();
  date.setMinutes(date.getMinutes() + time);

  return date;
}

export function hours_with_leading_zeros(dt) {
  return (dt.getHours() < 10 ? '0' : '') + dt.getHours();
}

export function hours_and_minutes_leading_zeros(dt: Date) {
  let hours = dt.getHours() < 10 ? '0' + dt.getHours() : dt.getHours();
  let minutes = dt.getMinutes() < 10 ? '0' + dt.getMinutes() : dt.getMinutes();
  return hours + ':' + minutes;
}

export function minutes_with_leading_zeros(dt) {
  return (dt.getMinutes() < 10 ? '0' : '') + dt.getMinutes();
}

export function castDateTimeWithSecondsAsBigInt(dateNow: Date) {
  return (
    dateNow.getFullYear() * 10000000000 +
    (dateNow.getMonth() + 1) * 100000000 +
    dateNow.getDate() * 1000000 +
    dateNow.getHours() * 10000 +
    dateNow.getMinutes() * 100 +
    dateNow.getSeconds()
  );
}

/**
 * Adds number of seconds to the Date
 * @param date Date
 * @param seconds Number of seconds to be added to the Date
 * @returns Updated Date
 */
export function addSecondsToDate(date: Date, seconds: number): Date {
  return new Date(new Date(date).setSeconds(date.getSeconds() + seconds));
}

/**
 * Validates date.
 * @param input Date in string or Date object.
 * @returns True when input is valid date. False when input is invalid date.
 */
export function isValidDate(input: string | Date): boolean {
  return new Date(input).toString() !== 'Invalid Date';
}

/**
 * Gets current (server) date and time value.
 * @returns Date with time as string.
 */
export function getServerDateAndTime(): string {
  return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

export function getDateFromJsDateObject(date: Date) {
  return date.toISOString().split('T')[0];
}

/**
 * This version doesnt transform to ISO
 * @param date 
 */
export function getDateFromJsDateObjectNotISO(date: Date) {
  return date.getFullYear()+'-'+addLeadingZeros(date.getMonth()+1,2)+'-'+addLeadingZeros(date.getDate(),2);
}

function addLeadingZeros(num, totalLength) {
  return String(num).padStart(totalLength, '0');
}

/**
 * Adds minutes to date using DayJS.
 * @param date Date
 * @param minutes minutes.
 */
export function addMinutes(date: Date, minutes: number) {
  return dayjs(date).add(minutes, 'minutes').toDate();
}

/**
 * Subtracts minutes to date using DayJS
 * @param date Date
 * @param minutes minutes.
 */
export function subtractMinutes(date: Date, minutes: number) {
  return dayjs(date).subtract(minutes, 'minutes').toDate();
}

export function getCurrentTimeString(d: Date) {
  return `${d.getUTCHours() > 9 ? d.getUTCHours() : '0' + d.getUTCHours()}:${
    d.getUTCMinutes() > 9 ? d.getUTCMinutes() : '0' + d.getUTCMinutes()
  }`;
}

export function getCurrentTimeStringLocalTimezone(d: Date) {
  return `${d.getHours() > 9 ? d.getHours() : '0' + d.getHours()}:${
    d.getMinutes() > 9 ? d.getMinutes() : '0' + d.getMinutes()
  }`;
}

export function convertTZ(date, tzString) {
  return new Date((typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", {timeZone: tzString}));   
}

export function returnMondaytoSundayNumber(data: Date){
  return (data.getUTCDay() + 6) % 7;
}

export function newDateUTC(date?: Date): Date{
  if(!date) { date = new Date(); }
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
}

/* Return the format YYYY-mm-dd */
export function transformDatetoString(date?: Date): string {
  if(!date){ date = new Date();}
  return  date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, "0") + '-' + date.getDate().toString().padStart(2, "0");
}

/* Return the format dd-mm-YYYY */
export function transformDatetoStringInverted(date?: Date): string {
  if(!date){ date = new Date();}
  return date.getDate().toString().padStart(2, "0") + '-' + (date.getMonth() + 1).toString().padStart(2, "0") + '-' +date.getFullYear();
}

/* Expect the format YYYY-mm-dd */
export function transformStringtoDate(date: string): Date {
  const breakDown= date.split('-').map(str => Number(str));
  return new Date(breakDown[0],breakDown[1]-1,breakDown[2],0,0,0,0);
}

/**
 * Returns string representation of the time passed as a Date object.
 * @param addSeconds Optionally ads second to the output. Default to `false`.
 * @returns Time string in format: 'hh:mm:ss'
 */
export function getTimeString(time: Date, addSeconds: boolean = false) {
  const h = (time.getHours() < 10 ? '0' : '') + time.getHours();
  const m = (time.getMinutes() < 10 ? '0' : '') + time.getMinutes();
  const s = addSeconds
    ? (time.getSeconds() < 10 ? '0' : '') + time.getSeconds()
    : '';
  return `${h}:${m}` + (addSeconds ? s : '');
}

/**
 * Parses a duration string in ISO8601 format and returns an object with the duration components.
 * @param iso8601Str duration string in ISO8601 format (e.g. P1Y2M3DT4H5M6S)
 * @returns object with the following properties:
 * - sign: '+' or '-'
 * - years: number of years
 * - months: number of months
 * - weeks: number of weeks
 * - days: number of days
 * - hours: number of hours
 * - minutes: number of minutes
 * - seconds: number of seconds
 */
export function parseISO8601Duration(iso8601Str: string) {
  const iso8601Regex = /(-)?P(?:([.,\d]+)Y)?(?:([.,\d]+)M)?(?:([.,\d]+)W)?(?:([.,\d]+)D)?(?:T(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?)?/
  const matches = iso8601Str.match(iso8601Regex);
  return {
    sign: matches[1] === undefined ? '+' : '-',
    years: matches[2] === undefined ? 0 : parseInt(matches[2]),
    months: matches[3] === undefined ? 0: parseInt(matches[3]),
    weeks: matches[4] === undefined ? 0 : parseInt(matches[4]),
    days: matches[5] === undefined ? 0: parseInt(matches[5]),
    hours: matches[6] === undefined ? 0 : parseInt(matches[6]),
    minutes: matches[7] === undefined ? 0: parseInt(matches[7]),
    seconds: matches[8] === undefined ? 0 : parseInt(matches[8]),
  }
}

/**
 * Adds a duration to a given date and returns the new date.
 * @param date - The date to which the duration will be added.
 * @param duration - The duration to add in ISO 8601 format.
 * @returns The new date after adding the duration.
 */
export function addDurationToDate(date: Date, duration: string) {
  const parsedDuration = parseISO8601Duration(duration);
  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() + parsedDuration.years);
  newDate.setMonth(newDate.getMonth() + parsedDuration.months);
  newDate.setDate(newDate.getDate() + parsedDuration.weeks * 7);
  newDate.setDate(newDate.getDate() + parsedDuration.days);
  newDate.setHours(newDate.getHours() + parsedDuration.hours);
  newDate.setMinutes(newDate.getMinutes() + parsedDuration.minutes);
  newDate.setSeconds(newDate.getSeconds() + parsedDuration.seconds);
  return newDate;
}

export const checkDateForColorCoding = (incomingDate: string | Date): OrderDeliveryStatusEnum|string => {
  const toReturnIfNone = 'Empty date or invalid value'; // just to pass the *ngIf validation in case is used in an ngIf to store its results as a variable
  const now = dayjs();

  let date: dayjs.Dayjs;
  if (typeof incomingDate === 'string' ) {
    date = dayjs(incomingDate, 'YYYY-MM-DD');
  } else if (incomingDate instanceof Date) {
    date = dayjs(incomingDate);
  } else {
    return toReturnIfNone;
  }

  if (date.isBefore(now, 'day')) {
    return OrderDeliveryStatusEnum.ORDER_IN_THE_PAST;
  }
  if (date.isSame(now, 'day') ) {
    return OrderDeliveryStatusEnum.ORDER_TODAY;
  }
  if (date.isAfter(now, 'day')) {
    return OrderDeliveryStatusEnum.ORDER_IN_THE_FUTURE;
  }

  return toReturnIfNone;
}